/**
 * RÉGLAGES SOIRÉES › BILLETTERIES — ce que la Save Bar compare, sans React.
 *
 * Sorti de la page (revue du 19/10/2026) pour être testé : un réglage oublié
 * dans `memes` ne fait jamais apparaître la Save Bar, et son choix est perdu
 * sans un mot. Le test change chaque champ un par un et attend « différent ».
 */

export interface Reglages {
  live_count: 'NIGHT' | 'SOIREE'
  show_dashboard: boolean
  show_cockpit: boolean
  include_in_analytics: boolean
  group_per_night: boolean
  pretix_label: string
  /* Rattachement à la soirée du club (19/10/2026). */
  attach_mode: 'CERTAIN_AND_PROBABLE' | 'CERTAIN_ONLY' | 'NEVER'
  attached_display: 'HIDDEN' | 'INDENTED'
  soiree_count: 'WITH_ATTACHED' | 'SOIREE_ONLY'
  announce_auto_attach: boolean
  /* Le graphique de la soirée (19/10/2026). */
  chart_scope: 'NIGHT' | 'SOIREE'
  chart_breakdown: 'BILLETTERIE' | 'NONE'
  chart_hourly_days: number
  color_shotgun: CouleurBilletterie
  color_pretix: CouleurBilletterie
  n1_reference: 'NIGHT_VS_SHOTGUN' | 'SHOTGUN_VS_SHOTGUN'
  bde_expected: boolean
  bde_rate_mode: 'AUTO' | 'FIXED'
  bde_rate_pct: number
  /* Les extras (19/10/2026). */
  show_no_show: boolean
  show_bar_formules: boolean
  announce_before_night: boolean
  announce_horizon_days: number
  association_label: boolean
  association_max_pct: number
  /* La tuile Billetterie d'une soirée association (20/10/2026). */
  association_tile: 'NIGHT' | 'CLUB' | 'HIDDEN'
  association_hide_empty: boolean
  /* L'alerte « à rattacher avant la nuit » (20/10/2026). */
  undecided_alert: 'DAY_BEFORE' | 'HOURS_BEFORE' | 'NEVER'
  undecided_alert_time: string
  undecided_alert_hours: number
  undecided_alert_no_club: boolean
  undecided_alert_patience: boolean
  undecided_alert_patience_hours: number
  undecided_alert_reminder_hours: number
}

export type CouleurBilletterie = 'violet' | 'cyan' | 'emerald' | 'amber' | 'rose' | 'blue'
export const COULEURS: ReadonlyArray<{ value: CouleurBilletterie; label: string }> = [
  { value: 'violet', label: 'Violet' }, { value: 'cyan', label: 'Cyan' }, { value: 'emerald', label: 'Vert' },
  { value: 'amber', label: 'Ambre' }, { value: 'rose', label: 'Rose' }, { value: 'blue', label: 'Bleu' },
]
export const JOURS_MIN = 1
export const JOURS_MAX = 14
export const TAUX_MIN = 50
export const TAUX_MAX = 100
export const HORIZON_MIN = 1
export const HORIZON_MAX = 60
export const PART_CLUB_MIN = 0
export const PART_CLUB_MAX = 30
/* Les bornes de l'alerte « avant la nuit » — les mêmes que le serveur (`billetteries.ts`). */
export const ALERTE_HEURES_MIN = 1
export const ALERTE_HEURES_MAX = 168
export const PATIENCE_HEURES_MIN = 1
export const PATIENCE_HEURES_MAX = 48
export const RAPPEL_HEURES_MAX = 24
const HEURE_RX = /^([01]\d|2[0-3]):([0-5]\d)$/

/** Ce qui bloque l'enregistrement, en clair ; `null` = rien. */
export function invalide(r: Reglages): string | null {
  if (r.pretix_label.trim() === '') return 'Donnez un libellé aux entrées des associations.'
  if (!Number.isInteger(r.chart_hourly_days) || r.chart_hourly_days < JOURS_MIN || r.chart_hourly_days > JOURS_MAX) {
    return `Heure par heure : un nombre de jours entre ${JOURS_MIN} et ${JOURS_MAX}.`
  }
  if (!Number.isInteger(r.bde_rate_pct) || r.bde_rate_pct < TAUX_MIN || r.bde_rate_pct > TAUX_MAX) {
    return `Taux de venue : un pourcentage entier entre ${TAUX_MIN} et ${TAUX_MAX}.`
  }
  if (!Number.isInteger(r.announce_horizon_days) || r.announce_horizon_days < HORIZON_MIN || r.announce_horizon_days > HORIZON_MAX) {
    return `Annonce avant la nuit : un nombre de jours entre ${HORIZON_MIN} et ${HORIZON_MAX}.`
  }
  if (!Number.isInteger(r.association_max_pct) || r.association_max_pct < PART_CLUB_MIN || r.association_max_pct > PART_CLUB_MAX) {
    return `« Soirée association » : un pourcentage entier entre ${PART_CLUB_MIN} et ${PART_CLUB_MAX}.`
  }
  if (!HEURE_RX.test(r.undecided_alert_time.trim())) return 'L’heure de l’alerte s’écrit HH:MM, par exemple 14:00.'
  if (!Number.isInteger(r.undecided_alert_hours) || r.undecided_alert_hours < ALERTE_HEURES_MIN || r.undecided_alert_hours > ALERTE_HEURES_MAX) {
    return `Alerte avant l’ouverture : un nombre d’heures entre ${ALERTE_HEURES_MIN} et ${ALERTE_HEURES_MAX}.`
  }
  if (!Number.isInteger(r.undecided_alert_patience_hours) || r.undecided_alert_patience_hours < PATIENCE_HEURES_MIN || r.undecided_alert_patience_hours > PATIENCE_HEURES_MAX) {
    return `Attente de la première vente : un nombre d’heures entre ${PATIENCE_HEURES_MIN} et ${PATIENCE_HEURES_MAX}.`
  }
  if (!Number.isInteger(r.undecided_alert_reminder_hours) || r.undecided_alert_reminder_hours < 0 || r.undecided_alert_reminder_hours > RAPPEL_HEURES_MAX) {
    return `Rappel le jour même : un nombre d’heures entre 0 et ${RAPPEL_HEURES_MAX} (0 = pas de rappel).`
  }
  if (r.color_shotgun === r.color_pretix) return 'Shotgun et Pretix ont la même couleur : on ne les distinguerait plus sur le graphique.'
  return null
}

export const DEFAUT: Reglages = {
  live_count: 'NIGHT', show_dashboard: true, show_cockpit: true,
  include_in_analytics: false, group_per_night: true, pretix_label: 'Entrées BDE (Pretix)',
  attach_mode: 'CERTAIN_AND_PROBABLE', attached_display: 'HIDDEN', soiree_count: 'WITH_ATTACHED', announce_auto_attach: true,
  chart_scope: 'NIGHT', chart_breakdown: 'BILLETTERIE', chart_hourly_days: 4, color_shotgun: 'violet', color_pretix: 'cyan',
  n1_reference: 'NIGHT_VS_SHOTGUN', bde_expected: false, bde_rate_mode: 'AUTO', bde_rate_pct: 90,
  show_no_show: true, show_bar_formules: true, announce_before_night: true, announce_horizon_days: 14,
  association_label: true, association_max_pct: 5,
  association_tile: 'NIGHT', association_hide_empty: true,
  undecided_alert: 'DAY_BEFORE', undecided_alert_time: '14:00', undecided_alert_hours: 24, undecided_alert_no_club: true,
  undecided_alert_patience: true, undecided_alert_patience_hours: 6, undecided_alert_reminder_hours: 0,
}

/** Les réglages à l'écran sont-ils ceux du serveur ? (`false` = la Save Bar s'affiche.) */
export const memes = (a: Reglages, b: Reglages): boolean =>
  a.live_count === b.live_count && a.show_dashboard === b.show_dashboard && a.show_cockpit === b.show_cockpit
  && a.include_in_analytics === b.include_in_analytics && a.group_per_night === b.group_per_night
  && a.pretix_label.trim() === b.pretix_label
  && a.attach_mode === b.attach_mode && a.attached_display === b.attached_display
  && a.soiree_count === b.soiree_count && a.announce_auto_attach === b.announce_auto_attach
  && a.chart_scope === b.chart_scope && a.chart_breakdown === b.chart_breakdown && a.chart_hourly_days === b.chart_hourly_days
  && a.color_shotgun === b.color_shotgun && a.color_pretix === b.color_pretix && a.n1_reference === b.n1_reference
  && a.bde_expected === b.bde_expected && a.bde_rate_mode === b.bde_rate_mode && a.bde_rate_pct === b.bde_rate_pct
  && a.show_no_show === b.show_no_show && a.show_bar_formules === b.show_bar_formules
  && a.announce_before_night === b.announce_before_night && a.announce_horizon_days === b.announce_horizon_days
  && a.association_label === b.association_label && a.association_max_pct === b.association_max_pct
  && a.association_tile === b.association_tile && a.association_hide_empty === b.association_hide_empty
  && a.undecided_alert === b.undecided_alert && a.undecided_alert_time.trim() === b.undecided_alert_time
  && a.undecided_alert_hours === b.undecided_alert_hours && a.undecided_alert_no_club === b.undecided_alert_no_club
  && a.undecided_alert_patience === b.undecided_alert_patience && a.undecided_alert_patience_hours === b.undecided_alert_patience_hours
  && a.undecided_alert_reminder_hours === b.undecided_alert_reminder_hours

/* ═══ Ce que la tuile Analytics fixe (revue du 19/10/2026) ═══ */

export interface ChoixFixes { bucket_s?: string; group_by?: string; kind?: string }
export type ChoixFixesParAppareil = Partial<Record<'desktop' | 'mobile', ChoixFixes>>

const PAS: Record<string, string> = { '60': 'minute', '900': '15 minutes', '1800': '30 minutes', '3600': '1 heure', '86400': 'jour' }
const VENTILATIONS: Record<string, string> = {
  none: 'sans ventilation', billetterie: 'par billetterie', product: 'par tarif', channel: 'par canal', sale_mode: 'prévente / sur place',
}
const FORMES: Record<string, string> = {
  bars: 'barres', grouped: 'barres groupées', stacked: 'barres empilées', line: 'courbe', area: 'aire',
  cumulative: 'cumul', stacked_cumulative: 'cumul empilé',
}

/**
 * L'avertissement de la page des réglages : la disposition par défaut de la
 * page Soirée a FIXÉ un choix sur la tuile Analytics, qui passe avant ces
 * réglages. En production, l'échelle « 1 heure » enregistrée le 29/08 rendait
 * « heure par heure sous N jours » sans effet, sans que rien ne le dise.
 * `null` : rien n'est fixé, les réglages s'appliquent. PURE.
 */
export function avisTuileFixee(fixes: ChoixFixesParAppareil | null | undefined): string | null {
  const morceaux: string[] = []
  for (const [appareil, nom] of [['desktop', 'ordinateur'], ['mobile', 'téléphone']] as const) {
    const f = fixes?.[appareil]
    if (!f) continue
    const choix = [
      f.bucket_s ? `l’échelle de temps (${PAS[f.bucket_s] ?? `${f.bucket_s} s`})` : null,
      f.group_by ? `la ventilation (${VENTILATIONS[f.group_by] ?? f.group_by})` : null,
      f.kind ? `la représentation (${FORMES[f.kind] ?? f.kind})` : null,
    ].filter((x): x is string => x !== null)
    if (choix.length > 0) morceaux.push(`${nom} : ${choix.join(', ')}`)
  }
  if (morceaux.length === 0) return null
  return `La tuile Analytics de la page Soirée a des choix fixés (${morceaux.join(' ; ')}). Ils passent avant les réglages ci-dessous : `
    + 'pour que ceux-ci s’appliquent, remettez ces choix sur « Automatique » dans l’éditeur de la page Soirée.'
}

/** L'introduction de la section, fidèle au réglage choisi — PURE. */
export function introGraphique(scope: Reglages['chart_scope']): string {
  return scope === 'NIGHT'
    ? 'Sur une soirée qui a un événement rattaché, la tuile Analytics et la Timeline montrent toute la nuit, chaque billetterie dans sa couleur. Les prévisions restent celles de la soirée du club.'
    : 'Sur une soirée qui a un événement rattaché, la tuile Analytics et la Timeline montrent la soirée seule ; « Toute la nuit » reste à un clic dans la tuile Analytics. Les prévisions restent celles de la soirée du club.'
}

/**
 * La phrase qui résume l'alerte telle qu'elle est réglée — PURE. La
 * conséquence d'abord : quand le message part, et à qui.
 */
export function resumeAlerte(r: Pick<Reglages, 'undecided_alert' | 'undecided_alert_time' | 'undecided_alert_hours' | 'undecided_alert_patience' | 'undecided_alert_patience_hours' | 'undecided_alert_reminder_hours'>): string {
  if (r.undecided_alert === 'NEVER') return 'Aucune alerte : un événement que Gestion ne rattache pas seul attend dans « À décider » sans que personne ne soit prévenu.'
  const quand = r.undecided_alert === 'DAY_BEFORE'
    ? `la veille de la nuit à ${r.undecided_alert_time.trim().replace(':', ' h ').replace(/ h 00$/, ' h')}`
    : `${r.undecided_alert_hours} h avant l’ouverture`
  const patience = r.undecided_alert_patience
    ? ` Quand la seule raison est une soirée du club qui n’a encore rien vendu, Gestion attend sa première vente, au plus tard ${r.undecided_alert_patience_hours} h avant l’ouverture.`
    : ''
  const rappel = r.undecided_alert_reminder_hours > 0 ? ` Un rappel ${r.undecided_alert_reminder_hours} h avant l’ouverture si rien n’est décidé.` : ''
  return `Un message ${quand} (ou dès que l’événement paraît, s’il paraît plus tard), à ceux qui gèrent les soirées.${patience}${rappel}`
}
